import { useEffect, useLayoutEffect, lazy, Suspense } from 'react'
import { Box, Skeleton } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  enUS as datePickersEn,
  plPL as datePickersPl,
  deDE as datePickersDe,
} from '@mui/x-date-pickers/locales'
import { enGB as dateFnsEn, pl as dateFnsPl, de as dateFnsDe } from 'date-fns/locale'
import { router } from '@inertiajs/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { usePrevious } from 'ahooks'
import { appEvents } from '@/lib/appEvents'
import { getSharedProps, type WithSharedProps } from '@/lib/sharedProps'
import { setLocale } from '@/lib/intl'
import { setDefaultLocaleParam } from '@/lib/routes'
import { setDefaultShiftsDefinitionFromCustomer } from '@/lib/shiftTime'
import { queryClient } from '@/api/queryClient'
import { Devtools } from '@/components/Devtools'
import { DateTimeContextProvider } from '@/components/DateTime'
import { FlashNotifications } from '@/components/FlashNotifications'

import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

const SignedInRootLayout = import.meta.env.SSR
  ? (await import('@/components/RootLayout/SignedIn')).default
  : lazy(() => import('@/components/RootLayout/SignedIn'))

const SignedOutRootLayout = import.meta.env.SSR
  ? (await import('@/components/RootLayout/SignedOut')).default
  : lazy(() => import('@/components/RootLayout/SignedOut'))

const datePickerLocales = {
  en: datePickersEn.components.MuiLocalizationProvider.defaultProps.localeText,
  pl: datePickersPl.components.MuiLocalizationProvider.defaultProps.localeText,
  de: datePickersDe.components.MuiLocalizationProvider.defaultProps.localeText,
} as const

const dateFnsLocales = { en: dateFnsEn, pl: dateFnsPl, de: dateFnsDe } as const

type Props = WithSharedProps<{
  children: React.ReactNode
}>

function RootLayout({ locale, currentCustomer, currentUser, children }: Props) {
  const isSignedIn = !!currentUser
  const prevIsSignedIn = usePrevious(isSignedIn)

  useLayoutEffect(() => {
    setLocale(locale)
  }, [locale])

  useEffect(() => {
    if (typeof prevIsSignedIn === 'undefined') {
      return
    }

    if (isSignedIn && !prevIsSignedIn) {
      appEvents.emit('signedIn')
    } else if (!isSignedIn && prevIsSignedIn) {
      appEvents.emit('signedOut')
    }
  }, [isSignedIn, prevIsSignedIn])

  useEffect(() => {
    return router.on('navigate', event => {
      const sharedProps = getSharedProps(event.detail.page.props as any)
      setDefaultLocaleParam(sharedProps.localeParam)
      setDefaultShiftsDefinitionFromCustomer(sharedProps.currentCustomer)
    })
  }, [])

  const Layout = isSignedIn ? SignedInRootLayout : SignedOutRootLayout

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      localeText={datePickerLocales[locale]}
      adapterLocale={dateFnsLocales[locale]}
    >
      <QueryClientProvider client={queryClient}>
        <Devtools>
          <DateTimeContextProvider customerTimeZone={currentCustomer?.timeZone}>
            <Layout>
              <Suspense
                fallback={
                  <Box p={3}>
                    <Skeleton variant='rectangular' width='100%' height={500} />
                  </Box>
                }
              >
                {children}
              </Suspense>
              <FlashNotifications />
            </Layout>
          </DateTimeContextProvider>
        </Devtools>
      </QueryClientProvider>
    </LocalizationProvider>
  )
}

export default RootLayout
